import React from 'react';
import { Shield, Lock, Eye, Server, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const POLICIES = [
  {
    icon: Server,
    title: 'Tenant Isolation',
    description: 'U.S. Soccer data is scoped to the authenticated portal context.',
    bullets: [
      'Access is restricted via authenticated accounts and portal-level configuration.',
      'No cross-client private inputs are displayed or used in comparisons.',
      'All queries and exports are scoped to the authorized tenant.',
    ],
  },
  {
    icon: Eye,
    title: 'Input Privacy',
    description: 'Federation-supplied inputs are never surfaced in raw form.',
    bullets: [
      'Private inputs remain hidden; only aggregated outputs are shown.',
      'Benchmarks shown are public or modeled baselines, clearly labeled.',
      'No raw financial, contractual, or internal data is exposed in any view or export.',
    ],
  },
  {
    icon: Lock,
    title: 'Anti-Inference Formatting',
    description: 'Outputs are aggregated and formatted with rounding and banding to prevent reverse-engineering of underlying data points. Comparative views use bucketed ranges rather than precise figures.',
    bullets: null,
  },
  {
    icon: FileText,
    title: 'External Data Sources',
    description: 'No external live feeds are connected unless explicitly integrated and documented. All current metrics are derived from the in-platform modeled dataset. Any future live integrations will be flagged in the data provenance layer.',
    bullets: null,
  },
];

function PolicyBullets({ items }) {
  if (!items) return null;
  return (
    <ul className="mt-2 space-y-1">
      {items.map((b, j) => (
        <li key={j} className="flex items-start gap-1.5 text-2xs text-muted-foreground/80">
          <span className="mt-1.5 w-1 h-1 rounded-full bg-stable/40 flex-shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function PolicyCard({ policy }) {
  const Icon = policy.icon;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 border border-border/30">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs font-medium text-foreground">{policy.title}</p>
        <p className="text-2xs text-muted-foreground mt-1 leading-relaxed">{policy.description}</p>
        <PolicyBullets items={policy.bullets} />
      </div>
    </div>
  );
}

export function SecurityPrivacyPanel() {
  return (
    <div className="space-y-4" data-testid="security-privacy-panel">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-stable" />
        <h3 className="text-sm font-semibold text-foreground">Security & Privacy</h3>
        <Badge className="bg-stable/10 text-stable border-stable/30 border text-2xs">Active</Badge>
      </div>

      <div className="space-y-3">
        <PolicyCard policy={POLICIES[0]} />
        <PolicyCard policy={POLICIES[1]} />
        <PolicyCard policy={POLICIES[2]} />
        <PolicyCard policy={POLICIES[3]} />
      </div>

      <div className="pt-2 border-t border-border/30">
        <p className="text-2xs text-muted-foreground/60">
          PDF exports include "CONFIDENTIAL — US SOCCER PILOT" watermark and generation timestamp.
          For questions, contact your platform administrator.
        </p>
      </div>
    </div>
  );
}

export default SecurityPrivacyPanel;
